import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../components/AuthProvider';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import {
  Search, Download, Lock, LogOut, Globe, User as UserIcon,
  BookOpen, ShieldAlert, FileSearch, MessageSquare, CreditCard,
  Calendar, Sparkles, ChevronRight, Shield, ExternalLink, FileText
} from 'lucide-react';
import { HRHubFooter } from '../components/HRHubFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABEL = { standard: 'Standard', pro: 'Pro', elite: 'Elite' };
const TIER_COLORS = {
  standard: { bg: '#1A56E8', text: '#fff' },
  pro: { bg: '#F5C100', text: '#1a1a1a' },
  elite: { bg: '#1A2238', text: '#fff' },
  none: { bg: '#6B7280', text: '#fff' },
};

const QuickAccessTile = ({ icon: Icon, title, description, locked, lockedHint, onClick, onLockedClick, testid, badgeCount }) => (
  <button
    type="button"
    onClick={locked ? onLockedClick : onClick}
    data-testid={testid}
    className={`relative text-left p-5 rounded-xl border-2 transition-all duration-200 w-full ${
      locked
        ? 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/40'
        : 'border-gray-200 bg-white hover:border-blue-500 hover:shadow-md hover:-translate-y-0.5'
    }`}
  >
    {!locked && badgeCount > 0 && (
      <span
        className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold inline-flex items-center justify-center shadow ring-2 ring-white"
        data-testid={`${testid}-badge`}
        aria-label={`${badgeCount} new`}
      >
        {badgeCount > 99 ? '99+' : badgeCount}
      </span>
    )}
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-lg ${locked ? 'bg-gray-200' : 'bg-blue-50'}`}>
        {locked ? <Lock className="w-5 h-5 text-gray-400" /> : <Icon className="w-5 h-5 text-blue-600" />}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h3 className={`font-semibold ${locked ? 'text-gray-500' : 'text-gray-900'}`}>{title}</h3>
          {!locked && <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
        <p className={`text-sm mt-1 ${locked ? 'text-gray-400' : 'text-gray-600'}`}>{description}</p>
        {locked && lockedHint && (
          <p className="text-xs mt-2 font-medium" style={{ color: '#1A56E8' }}>
            {lockedHint} →
          </p>
        )}
      </div>
    </div>
  </button>
);

export const MemberPortal = () => {
  useDocumentTitle('DriveHR — HR Hub');
  const { user, logout, authHeader, refresh } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [alertsUnread, setAlertsUnread] = useState(0);
  const [recentTemplates, setRecentTemplates] = useState([]);

  // Helper that turns a Featured-Tools / Start-Here / Recent item into the
  // best deep-link target. If a template_id was picked in admin → go to the
  // library filtered to that exact template's name. If a free-form `link` was
  // entered → use that. Otherwise fall back to the library home.
  const itemTarget = (item) => {
    if (!item) return '/members/library';
    if (item.template_id) {
      // Template names get URL-encoded so library?q= filter matches them.
      const t = recentTemplates.find((x) => x.id === item.template_id);
      const label = t?.name || item.title || '';
      return label ? `/members/library?q=${encodeURIComponent(label)}` : '/members/library';
    }
    if (item.link) return item.link;
    if (item.title) return `/members/library?q=${encodeURIComponent(item.title)}`;
    return '/members/library';
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [d, c, r] = await Promise.all([
          axios.get(`${API}/portal/dashboard`, { headers: authHeader() }),
          axios.get(`${API}/portal/content-modules`),
          axios.get(`${API}/templates?limit=5`, { headers: authHeader() }).catch(() => ({ data: { templates: [] } })),
        ]);
        if (mounted) {
          setDashboard(d.data);
          setContent(c.data);
          setRecentTemplates(r.data.templates || []);
        }
      } catch (e) {
        console.error('Dashboard load failed', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    // Unread alerts count — silently no-op for Standard/no-tier (backend returns 0).
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portal/compliance-alerts/unread-count`, { headers: authHeader() });
        if (mounted) setAlertsUnread(data.unread || 0);
      } catch (_) { /* silent — not critical for the dashboard */ }
    })();
    return () => { mounted = false; };
  }, [authHeader]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const tier = dashboard?.membership_tier || null;
  const tierLabel = tier ? TIER_LABEL[tier] : 'No Active Plan';
  const tierColor = TIER_COLORS[tier || 'none'];
  const downloadsLimit = dashboard?.downloads_limit ?? 0;
  const downloadsUsed = dashboard?.downloads_used ?? 0;
  const remaining = downloadsLimit === -1 ? '∞' : Math.max(0, downloadsLimit - downloadsUsed);
  const resetAtISO = dashboard?.downloads_reset_at;
  // For active subscribers we show the actual billing-cycle renewal date;
  // when the backend can't determine it (rare missing-billing-data case) we
  // fall back to the legacy "on the 1st" phrasing.
  const resetLabel = (() => {
    if (!resetAtISO) return 'on the 1st';
    try {
      const d = new Date(resetAtISO);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return 'on the 1st'; }
  })();
  // Days-until-reset chip — natural-language fallbacks for today/tomorrow so
  // the copy reads gently rather than clinically. Hidden when we don't have
  // a real reset date or the user is on an unlimited plan.
  const daysUntilReset = (() => {
    if (!resetAtISO) return null;
    try {
      const d = new Date(resetAtISO);
      const now = new Date();
      const ms = d.getTime() - now.getTime();
      return Math.ceil(ms / (1000 * 60 * 60 * 24));
    } catch { return null; }
  })();
  const resetChipLabel = (() => {
    if (downloadsLimit === -1 || daysUntilReset === null) return null;
    if (daysUntilReset <= 0) return 'resets today';
    if (daysUntilReset === 1) return 'resets tomorrow';
    return `resets in ${daysUntilReset} days`;
  })();
  const showUpgrade = tier !== 'elite';

  const isProOrElite = tier === 'pro' || tier === 'elite';
  const isElite = tier === 'elite';
  const goToPlans = () => navigate('/#memberships');

  return (
    <div className="min-h-screen bg-gray-50" data-testid="member-portal">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="text-xl font-bold flex-shrink-0" style={{ color: '#1A56E8' }}>DriveHR</div>
            <span className="hidden sm:inline text-gray-300">|</span>
            <span className="hidden sm:inline text-gray-700 font-medium truncate">HR Hub</span>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === 'admin' && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="portal-admin-link" className="text-blue-600 hover:bg-blue-50">
                <Shield className="w-4 h-4 mr-1" />Admin
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="portal-public-site-btn"><Globe className="w-4 h-4 mr-1" />Public Site</Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} data-testid="portal-logout-btn"><LogOut className="w-4 h-4 mr-1" />Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Welcome banner */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 sm:p-8 text-white" data-testid="welcome-banner">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Welcome back, {user?.first_name || 'there'}</h1>
              <div className="mt-2 flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: tierColor.bg, color: tierColor.text }} data-testid="tier-badge">
                  {tierLabel}
                </span>
                {dashboard?.membership_status && tier && (
                  <span className="text-blue-100 text-sm capitalize">· {dashboard.membership_status}</span>
                )}
              </div>
              {showUpgrade && (
                <p className="mt-3 text-blue-100 text-sm" data-testid="upgrade-prompt">
                  {tier === null ? (
                    <>You don&apos;t have an active membership yet. <Link to="/#memberships" className="underline font-semibold text-white">Choose a plan</Link> to unlock the full library.</>
                  ) : (
                    <><Sparkles className="inline w-4 h-4 mr-1" /> Unlock unlimited downloads with Elite. <Link to="/#memberships" className="underline font-semibold text-white">Upgrade</Link></>
                  )}
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Global Search — submits to the Library page with ?q= */}
        <section className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-200" data-testid="global-search">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const q = search.trim();
              navigate(q ? `/members/library?q=${encodeURIComponent(q)}` : '/members/library');
            }}
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="search"
                placeholder="Search templates, guides, and resources..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 pr-24 h-12 text-base"
                data-testid="global-search-input"
              />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-md hover:bg-blue-50" data-testid="global-search-submit">
                Search
              </button>
            </div>
          </form>
        </section>

        {/* Download counter */}
        <section className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-200 flex items-center justify-between" data-testid="download-counter">
          <div>
            <div className="text-sm text-gray-500">Downloads this month</div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">
              {downloadsLimit === -1 ? <>Unlimited</> : <><span data-testid="downloads-used">{downloadsUsed}</span> <span className="text-gray-400">/</span> <span data-testid="downloads-limit">{downloadsLimit}</span></>}
            </div>
            <div className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              {downloadsLimit === -1 ? (
                <span>Enjoy unrestricted access.</span>
              ) : (
                <>
                  <span><span className="font-medium text-gray-700">{remaining}</span> remaining · resets {resetLabel}</span>
                  {resetChipLabel && (
                    <span
                      className="inline-block text-[11px] uppercase tracking-wide font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5"
                      data-testid="reset-countdown-chip"
                    >
                      {resetChipLabel}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
          <Download className="w-12 h-12 text-blue-100" />
        </section>

        {/* Quick Access tiles */}
        <section data-testid="quick-access">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Quick Access</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(() => {
              return <>
                <QuickAccessTile testid="tile-browse-library" icon={BookOpen} title="Browse Template Library" description="Find the right HR document, fast." onClick={() => navigate('/members/library')} />
                <QuickAccessTile testid="tile-compliance-alerts" icon={ShieldAlert} title="Compliance Alerts" description="Latest federal and state changes that affect your business." locked={!isProOrElite} lockedHint="Upgrade to Pro" onClick={() => navigate('/members/compliance')} onLockedClick={goToPlans} badgeCount={isProOrElite ? alertsUnread : 0} />
                <QuickAccessTile testid="tile-document-review" icon={FileSearch} title="Request Document Review" description="Submit a document for expert review (1/mo)." locked={!isElite} lockedHint="Elite only" onClick={() => navigate('/members/document-review')} onLockedClick={goToPlans} />
                <QuickAccessTile testid="tile-ask-drivehr" icon={MessageSquare} title="Ask DriveHR" description="Email-based HR guidance from real HR pros." locked={!isProOrElite} lockedHint="Upgrade to Pro" onClick={() => navigate('/members/ask')} onLockedClick={goToPlans} />
                <QuickAccessTile testid="tile-account" icon={CreditCard} title="Account & Billing" description="Manage plan, payment method, and invoices." onClick={() => navigate('/members/account')} />
              </>;
            })()}
          </div>
        </section>

        {/* Start Here — admin-editable starter essentials */}
        <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200" data-testid="start-here">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-gray-900">{content?.starter_essentials?.title || 'New to DriveHR? Start with these essentials'}</h2>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {(content?.starter_essentials?.items || []).map((item) => (
              <li key={item.id} data-testid={`start-here-${item.id}`}>
                <button
                  type="button"
                  onClick={() => navigate(itemTarget(item))}
                  className="w-full text-left flex items-center gap-2 p-3 rounded-lg border border-gray-100 hover:border-blue-300 hover:bg-blue-50 transition cursor-pointer group"
                  data-testid={`start-here-link-${item.id}`}
                >
                  <BookOpen className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="text-gray-800 group-hover:text-blue-700 flex-1">{item.title}</span>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500" />
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* Monthly HR Reminders — Pro/Elite only */}
        {isProOrElite ? (
          <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200" data-testid="monthly-reminders">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                {content?.monthly_reminders?.title || 'Monthly HR Reminders'}
              </h2>
              <span className="text-sm text-gray-500">{content?.monthly_reminders?.month_label}</span>
            </div>
            <ul className="space-y-2">
              {(content?.monthly_reminders?.items || []).map((item, i) => {
                // Backward-compat: legacy items were plain strings.
                const text = typeof item === 'string' ? item : item.text;
                const link = typeof item === 'string' ? null : item.link;
                const row = (
                  <>
                    <span className="text-blue-600 mt-1">•</span>
                    <span className="flex-1">{text}</span>
                    {link && <ExternalLink className="w-3.5 h-3.5 text-gray-400 mt-1 flex-shrink-0" />}
                  </>
                );
                return link ? (
                  <li key={i} data-testid={`reminder-${i}`}>
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-md px-2 py-1 -mx-2 transition"
                      data-testid={`reminder-link-${i}`}
                    >
                      {row}
                    </a>
                  </li>
                ) : (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700 px-2 py-1 -mx-2" data-testid={`reminder-${i}`}>
                    {row}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : (
          <section
            className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-6 text-center hover:border-blue-300 hover:bg-blue-50/30 transition cursor-pointer"
            onClick={goToPlans}
            data-testid="monthly-reminders-locked"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <Lock className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-700">Monthly HR Reminders</h2>
            </div>
            <p className="text-sm text-gray-500 mb-3">A curated checklist of what&apos;s due this month — deadlines, posters, audits, and more.</p>
            <span className="text-xs font-semibold" style={{ color: '#1A56E8' }}>Included with Pro &amp; Elite — Upgrade →</span>
          </section>
        )}

        {/* Featured HR Tools — admin-editable, clickable to deep-link into library */}
        <section data-testid="featured-tools">
          <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            {content?.featured_tools?.title || 'Featured HR Tools'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(content?.featured_tools?.items || []).map((tool) => (
              <button
                key={tool.id}
                type="button"
                onClick={() => navigate(itemTarget(tool))}
                className="text-left bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:border-blue-400 hover:shadow-md hover:-translate-y-0.5 transition cursor-pointer group"
                data-testid={`featured-tool-${tool.id}`}
              >
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-gray-900 group-hover:text-blue-700">{tool.title}</h3>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 flex-shrink-0 mt-0.5" />
                </div>
                <p className="text-sm text-gray-600 mt-1">{tool.description}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Recently Added — auto-populated from the templates collection */}
        <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200" data-testid="recently-added">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Recently Added Resources</h2>
          {recentTemplates.length === 0 ? (
            <p className="text-sm text-gray-500">New templates and resources will appear here as we publish them.</p>
          ) : (
            <ul className="space-y-2">
              {recentTemplates.map((t) => (
                <li key={t.id} data-testid={`recent-${t.id}`}>
                  <button
                    type="button"
                    onClick={() => navigate(`/members/library?q=${encodeURIComponent(t.name)}`)}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-blue-300 hover:bg-blue-50 transition cursor-pointer group"
                    data-testid={`recent-link-${t.id}`}
                  >
                    <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 group-hover:text-blue-700 truncate">{t.name}</div>
                      <div className="text-xs text-gray-500">
                        Added {new Date(t.created_at || t.added_at).toLocaleDateString()}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* HR Resource Library — category quick-links into /members/library */}
        <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200" data-testid="resource-library">
          <h2 className="text-xl font-bold text-gray-900 mb-1">HR Resource Library</h2>
          <p className="text-sm text-gray-600 mb-4">Organized by category. {tier === 'standard' ? 'Standard members see the 50 essential core templates.' : isProOrElite ? 'You have full access to all 245 templates.' : 'Subscribe to access the library.'}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {[
              { id: 'hiring-onboarding', label: 'Hiring & Onboarding' },
              { id: 'hr-policies', label: 'HR Policies' },
              { id: 'performance-development', label: 'Performance & Development' },
              { id: 'benefits-compensation', label: 'Benefits & Compensation' },
              { id: 'termination-offboarding', label: 'Termination & Offboarding' },
              { id: 'compliance-legal', label: 'Compliance & Legal' },
              { id: 'culture-dei-engagement', label: 'Culture, DEI & Engagement' },
              { id: 'hr-operations', label: 'HR Operations' },
            ].map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => navigate(`/members/library?category=${cat.id}`)}
                data-testid={`library-cat-${cat.id}`}
                className="text-left px-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer"
              >
                {cat.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-4">Browse the full library at <a href="/members/library" className="text-blue-600 hover:underline">Template Library</a>.</p>
        </section>
      </main>
      <HRHubFooter />
    </div>
  );
};
